import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import {makeStyles, withTheme} from '@material-ui/core/styles';
import {useMediaQuery, Box, Typography, Avatar, withStyles} from '@material-ui/core';

import {ResponsiveHeader, SidePanel, Map, LogoHorizontalNegativo, BaseMapList} from '@geomatico/geocomponents';

import {
  WIDESCREEN_STEP,
  INITIAL_VIEWPORT,
  DRAWER_WIDTH,
  MODES, INITIAL_MAPSTYLE_URL, MAPSTYLES
} from '../config';

import {FlyToInterpolator, Marker, WebMercatorViewport, GeolocateControl} from 'react-map-gl';
import SectionTitle from '../components/SectionTitle';
import {DirectionsBike, DirectionsWalk} from '@material-ui/icons';
import Legend from '../components/Legend';

import './marker.css';
import {getIsochrones, ors_modes} from '../utils/ors';
import {donut, outerline} from '../utils/donutea';

const useStyles = makeStyles((theme) => ({
  sidebar: {
    height: '90vh'
  },
  mapContent: {
    flexGrow: 1,
    padding: 0,
    position: 'absolute',
    top: theme.mixins.toolbar.minHeight,
    bottom: 0,
    right: 0,
    left: ({isSidePanelOpen, widescreen}) => isSidePanelOpen && widescreen ? DRAWER_WIDTH : 0,
  },
  layerlabel: {
    letterSpacing: 1.05,
    paddingLeft: 25,
  },
  legend: {
    fontSize: 12,
    marginLeft: 22
  },
  legendIcon: {
    marginLeft: 25,
  }
}));

const modes = {
  'foot': ors_modes['foot-walking'],
  'cycling': ors_modes['cycling-regular']
};

const CustomAvatar = withStyles((theme) => ({
  root: {
    background: theme.palette.primary.main,
    border: '1px solid #0003',
    filter: 'drop-shadow(2px 2px 1px rgba(0,0,0,.2))'
  }
}))(Avatar);

const App = () => {
  const widescreen = useMediaQuery(`(min-width: ${WIDESCREEN_STEP})`, {noSsr: true});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);
  const classes = useStyles({isSidePanelOpen, widescreen});
  const [selectedStyleUrl, setSelectedStyleUrl] = useState(INITIAL_MAPSTYLE_URL);

  const [mode, setMode] = useState('foot');
  const [lngLat, setLngLat] = useState();

  const [isochrones, setIsochrones] = useState({
    'type': 'FeatureCollection',
    'features': []
  });

  const handleMapClick = ({lngLat}) => setLngLat(lngLat);

  useEffect(() => {
    lngLat &&
    getIsochrones(lngLat[0], lngLat[1], {mode: modes[mode]})
      .then(setIsochrones);
  }, [lngLat, mode]);

  const toggleMode = () => setMode(mode === 'foot' ? 'cycling' : 'foot');

  const onViewportChange = (viewport) =>
    setViewport({
      ...viewport
    });

  const handleClose = () => setIsSidePanelOpen(!isSidePanelOpen);

  const sources = useMemo(() => ({
    'donut': {
      type: 'geojson',
      data: donut(isochrones)
    },
    'outer': {
      type: 'geojson',
      data: outerline(isochrones)
    }
  }), [isochrones]);

  useEffect(() => {
    if (sources.donut.data.bbox) {
      const [xmin, ymin, xmax, ymax] = sources.donut.data.bbox;
      const newViewport = new WebMercatorViewport(viewport)
        .fitBounds([[xmin, ymin], [xmax, ymax]], {
          padding: 50
        });
      onViewportChange({
        ...newViewport,
        transitionDuration: 1000,
        transitionInterpolator: new FlyToInterpolator()
      });
    }
  }, [sources]);

  const layers = useMemo(() => (mode ?
    [{
      'id': 'isochrone-areas',
      'source': 'donut',
      'type': 'fill',
      'paint': {
        'fill-opacity': 0.17,
        'fill-color': [
          'match',
          ['get', 'value'],
          300, '#00ff00',
          600, '#ffff00',
          900, '#ff0000',
          '#ff00ff'
        ]
      }
    }, {
      'id': 'isochrone-limits',
      'source': 'outer',
      'type': 'line',
      'paint': {
        'line-width': 2,
        'line-opacity': 0.7,
        'line-color': [
          'match',
          ['get', 'value'],
          300, '#00aa00',
          600, '#aaaa00',
          900, '#aa0000',
          '#aa00aa'
        ]
      }
    }, {
      'id': 'isochrone-labels',
      'source': 'outer',
      'type': 'symbol',
      'minzoom': 13,
      'layout': {
        'symbol-placement': 'line',
        'text-font': ['Open Sans Regular'],
        'text-field': ['concat', ['/', ['get', 'value'], 60], ' min'],
        'text-size': 12,
        'text-offset': [0, -0.5]
      },
      'paint': {
        'text-color': [
          'match',
          ['get', 'value'],
          300, '#00aa00',
          600, '#aaaa00',
          900, '#aa0000',
          '#aa00aa'
        ]
      }
    }]
    : []
  ), []);

  const clickedComponent = useMemo(() => (lngLat && [
    <Marker
      key={'clickedPoint'}
      captureDrag={false}
      latitude={lngLat[1]}
      longitude={lngLat[0]}
      offsetLeft={-20} offsetTop={-20}>
      <CustomAvatar>
        {MODES.find(({id}) => id === mode).icon}
      </CustomAvatar>
    </Marker>
  ]), [lngLat, mode]);

  return (
    <>
      <ResponsiveHeader
        logo={<LogoHorizontalNegativo/>}
        logoStyleProps={{
          width: 170,
          paddingTop: 0,
        }}
        onStartIconClick={widescreen ? undefined : handleClose}
      >
      </ResponsiveHeader>
      <SidePanel
        drawerWidth={DRAWER_WIDTH}
        anchor="left"
        isOpen={isSidePanelOpen}
        onClose={handleClose}
        widescreen={widescreen}
      >
        <div>
          <Box mb={6} display='flex' flexDirection='column' justifyContent='space-between'>
            <div>
              <Typography variant='body1' color='textSecondary' style={{padding: 8, textAlign: 'justify'}}>
                Haz Click en el mapa para ver hasta dónde puedes llegar
                a pie <DirectionsWalk fontSize='inherit'/> o en bicicleta <DirectionsBike fontSize='inherit'/>.
              </Typography>
            </div>
            <SectionTitle title="CAPAS BASE"/>
            <BaseMapList
              styles={MAPSTYLES}
              selectedStyleUrl={selectedStyleUrl}
              onStyleChange={(basemap) => setSelectedStyleUrl(basemap)}
              typographyStyleProps={{fontSize: 14}}
            />
          </Box>
        </div>
      </SidePanel>
      <main className={classes.mapContent}>
        <Map
          mapStyle={selectedStyleUrl}
          viewport={viewport}
          onViewportChange={onViewportChange}
          sources={sources}
          layers={layers}
          onClick={handleMapClick}
        >
          <GeolocateControl
            style={{right: 8, top: 16}}
            positionOptions={{enableHighAccuracy: true}}
            trackUserLocation={true}
            showAccuracyCircle={false}
          />
          {clickedComponent}
        </Map>
        {mode && <Legend
          icon={MODES.find(({id}) => id === mode).icon}
          modeLabel={MODES.find(({id}) => id === mode).label}
          onClick={toggleMode}
        />
        }
      </main>
    </>
  );
};

App.propTypes = {
  theme: PropTypes.object.isRequired
};

export default withTheme(App);
